import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../shared/lib/cn';
import { Badge } from '../../shared/ui/Badge';
import { EmptyState } from '../../shared/ui/EmptyState';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { completeFollowUp } from './activity-api';
import { tickChecklistItem } from './checklist-api';
import s from './MyWorkPanel.module.css';
import { groupWork, summariseWork } from './work-queue';
import { listMyWork, type WorkItem, WORK_KEYS } from './workflow-api';
import { WorkQueueRow } from './WorkQueueRow';

interface Props {
  instituteId: string;
}

/** Rows shown before the panel defers to the full page. */
const PREVIEW_LIMIT = 8;
/** Fetched, so "See all N" can say how much is behind the fold. */
const PREVIEW_FETCH = 40;

/**
 * The work queue, previewed on the dashboard.
 *
 * One queue, not two: follow-ups on the timeline, board cards and checklist
 * items with a date, and files students have sent are the same question —
 * what needs doing today — so they share one due-date-ordered list. Phase 174
 * gave the list its own page with the actions on every row; this panel keeps
 * the first eight, a tick, and the way there.
 */
export function MyWorkPanel({ instituteId }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [mine, setMine] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: WORK_KEYS.myWork(instituteId, mine, false),
    queryFn: () => listMyWork(instituteId, mine, false, PREVIEW_FETCH),
    enabled: instituteId !== '',
    staleTime: 30 * 1000,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['institute', 'my_work'] });
  }, [queryClient]);

  const tick = useMutation({
    mutationFn: async (row: WorkItem) => {
      if (row.kind === 'follow_up') await completeFollowUp(row.id, true);
      else await tickChecklistItem(row.id, true);
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const rows = query.data ?? [];
  const now = dayjs();
  const summary = summariseWork(rows, now);
  const shown = rows.slice(0, PREVIEW_LIMIT);
  const groups = groupWork(shown, 'due', now);
  const hidden = rows.length - shown.length;

  return (
    <section className={s.root} aria-labelledby="my-work-title">
      <div className={s.head}>
        <h2 id="my-work-title" className={s.title}>
          Work queue
        </h2>
        {summary.overdue > 0 ? (
          <Link to="/institute/work?view=all&state=overdue" className={s.statLink}>
            <Badge tone="danger">{summary.overdue} overdue</Badge>
          </Link>
        ) : null}
        {summary.toGrade > 0 ? (
          <Link to="/institute/work?view=to-grade" className={s.statLink}>
            <Badge tone="warning">{summary.toGrade} to grade</Badge>
          </Link>
        ) : null}
        {summary.toReview > 0 ? (
          <Link to="/institute/work?view=to-review" className={s.statLink}>
            <Badge tone="warning">{summary.toReview} to review</Badge>
          </Link>
        ) : null}
        <label className={s.toggle} htmlFor="my-work-mine">
          <input
            id="my-work-mine"
            type="checkbox"
            checked={mine}
            onChange={(event) => {
              setMine(event.target.checked);
            }}
          />
          Only mine
        </label>
        <Link to="/institute/work" className={s.openAll}>
          Open Work →
        </Link>
      </div>

      {error !== null ? <StatusBanner tone="error">{error}</StatusBanner> : null}
      {query.isError ? (
        <StatusBanner tone="error">Could not load the work queue.</StatusBanner>
      ) : null}

      {query.isPending ? (
        <p className={s.loading}>Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          variant="plain"
          title={mine ? 'Nothing assigned to you' : 'Nothing outstanding'}
          hint="Follow-ups you schedule on a student’s timeline, board cards with a due date, files students send, attempts waiting to be graded and assignments nobody has handed in all land here."
        />
      ) : (
        <div className={s.groups}>
          {groups.map((group) => (
            <div key={group.key} className={s.group}>
              <h3 className={cn(s.groupHeading, group.urgent && s.groupHeadingOverdue)}>
                {group.label}
              </h3>
              <ul className={s.list}>
                {group.items.map((row) => (
                  <WorkQueueRow
                    key={`${row.kind}-${row.id}`}
                    row={row}
                    now={now}
                    staff={[]}
                    currentUserId={null}
                    busy={tick.isPending}
                    compact
                    onTick={(item) => {
                      tick.mutate(item);
                    }}
                    onAssign={() => undefined}
                    onSnooze={() => undefined}
                    onOpenFile={() => undefined}
                    onReview={() => undefined}
                  />
                ))}
              </ul>
            </div>
          ))}
          {hidden > 0 ? (
            <Link to="/institute/work" className={s.seeAll}>
              See all {String(rows.length)}
              {rows.length === PREVIEW_FETCH ? '+' : ''} in Work →
            </Link>
          ) : null}
        </div>
      )}
    </section>
  );
}
